import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { buyerSubmissionsApi, type BuyerSubmissionListParams } from "@/api/buyers";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { submissionPhaseLadder } from "../../submissionPhases";
import { AttentionTile } from "../../indexTiles/AttentionTile";
import { IndexTileDeck, Measure, MeasureTile } from "../../indexTiles/MeasureTile";
import { StageDistributionTile } from "../../indexTiles/StageDistributionTile";
import { VolumeTile } from "../../indexTiles/VolumeTile";
import type { StageSegment, StageTone } from "../../indexTiles/tileBits";
import { QueryErrorState } from "../../queryStates";
import { listHref } from "../reportLinks";

/** Mirrors of the list page's attention windows. */
const BROKER_RESPONSE_DAYS = 7;
const QUIET_DAYS = 7;

/** The rung → colour map, declared per phase (the list page's, copied). */
const SUBMISSION_PHASE_TONE: Record<string, StageTone> = {
  selection: "early",
  outreach: "working",
  package: "advanced",
  diligence: "nearly",
  offer: "committed",
  won: "won",
};

/**
 * The four submission tiles, over the list page's stats read.
 *
 * <p>The submissions list query carries no team parameter (a submission is
 * addressed by buyer and listing, and its team is the buyer's), so this tab
 * reads the whole book whatever the page's team filter says, and says so.</p>
 */
export function SubmissionsReportTab() {
  const navigate = useNavigate();
  // R6 — a report counts the whole book, archived included: archiving a
  // submission is display-only and must not move these figures (D6).
  const params = useMemo<BuyerSubmissionListParams>(
    () => ({ page: 1, includeArchived: true }),
    [],
  );
  const stats = useQuery({
    queryKey: buyerSubmissionKeys.stats(params),
    queryFn: () => buyerSubmissionsApi.stats(params),
  });

  const stageSegments = useMemo<StageSegment[]>(() => {
    const byStatus = stats.data?.byStatus ?? {};
    return submissionPhaseLadder.phases.map((p) => ({
      key: p.key,
      label: p.label,
      count: p.statuses.reduce((sum, s) => sum + (byStatus[s] ?? 0), 0),
      tone: SUBMISSION_PHASE_TONE[p.key] ?? "working",
    }));
  }, [stats.data]);

  if (stats.isError) {
    return (
      <QueryErrorState
        what="submission figures"
        onRetry={() => void stats.refetch()}
        retrying={stats.isFetching}
      />
    );
  }

  return (
    <>
      <p className="text-caption text-gray-mid mb-space-3">
        Submissions are counted across every team.
      </p>
      <IndexTileDeck>
        <StageDistributionTile
          title="Conversation stage"
          segments={stageSegments}
          // The tiles count archived rows (R6), so the drill-through shows them too.
          onSelect={(key) => navigate(listHref("/buyer-submissions", { phase: key, archived: "1", scope: "all" }))}
          loading={stats.isLoading}
        />

        <AttentionTile
          items={[
            {
              key: "awaiting-broker",
              label: `Awaiting broker response ${BROKER_RESPONSE_DAYS}+ days`,
              count: stats.data?.awaitingBrokerResponse ?? 0,
              severity: "overdue",
            },
            {
              key: "package-not-sent",
              label: "Package generated, not sent",
              count: stats.data?.packageGeneratedNotSent ?? 0,
            },
            {
              key: "nda-outstanding",
              label: "NDA outstanding",
              count: stats.data?.ndaOutstanding ?? 0,
            },
            {
              key: "quiet",
              label: `No activity in ${QUIET_DAYS} days`,
              count: stats.data?.noRecentActivity ?? 0,
            },
          ]}
          onSelect={(key) => navigate(listHref("/buyer-submissions", { card: key, archived: "1", scope: "all" }))}
          loading={stats.isLoading}
        />

        <VolumeTile
          title="Submissions started"
          points={stats.data?.createdByMonth ?? []}
          loading={stats.isLoading}
        />

        <MeasureTile title="Velocity">
          <Measure
            label="Median days in stage"
            value={
              stats.data?.medianDaysSinceLastMilestone == null
                ? "—"
                : Math.round(stats.data.medianDaysSinceLastMilestone)
            }
            detail={
              stats.data?.medianSampleSize
                ? `across ${stats.data.medianSampleSize} live submissions`
                : "no live submissions"
            }
          />
        </MeasureTile>
      </IndexTileDeck>
    </>
  );
}
